import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useLivePoll — silent polling hook with visibility-pause + manual refresh.
 *
 * @param {() => Promise<any>} fetcher — async fn returning the latest payload
 * @param {object}              opts
 * @param {number}              opts.intervalMs — poll interval (default 30s)
 * @param {any[]}               opts.deps       — re-init when these change
 *
 * Returns: { data, updatedAt, busy, refresh }
 *  - Pauses while `document.visibilityState !== "visible"` to save bandwidth
 *  - First fetch runs immediately on mount / dep change
 *  - When the tab returns to visible, fetches immediately then resumes interval
 */
export function useLivePoll(fetcher, { intervalMs = 30000, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const result = await fetcherRef.current();
      setData(result);
      setUpdatedAt(Date.now());
    } catch {
      /* swallow — keep previous data on transient errors */
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    let interval;
    let cancelled = false;

    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === "visible" && !cancelled) run();
      }, intervalMs);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        run();
        start();
      } else {
        stop();
      }
    };

    run(); // initial
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);

  return { data, updatedAt, busy, refresh: run };
}
